import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EndpointSidebar } from '@/components/data-browser/endpoint-sidebar';
import { RequestBuilder } from '@/components/data-browser/request-builder';
import { ResponseTabs } from '@/components/data-browser/response-tabs';
import { getUserConnections, fetchGatewayData, postGatewayData } from '@/lib/integrations/api';
import type {
  UserConnection,
  ProviderEndpoint,
  RequestConfig,
  ExecutionResult,
} from '@/lib/integrations/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_dashboard/data/$alias')({
  component: ConnectionExplorerPage,
});

function ConnectionExplorerPage() {
  const { alias } = Route.useParams();
  const navigate = useNavigate();

  const [connection, setConnection] = useState<UserConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedEndpoint, setSelectedEndpoint] = useState<ProviderEndpoint | null>(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [lastRequestConfig, setLastRequestConfig] = useState<RequestConfig | null>(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load connection
  useEffect(() => {
    async function loadConnection() {
      setLoading(true);
      setError(null);
      try {
        const connections = await getUserConnections();
        const found = connections.find(c => c.alias === alias);
        if (!found) {
          setError('Connection not found');
        } else {
          setConnection(found);
        }
      } catch {
        setError('Failed to load connection');
      } finally {
        setLoading(false);
      }
    }
    loadConnection();
  }, [alias]);

  // Load sidebar preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dataBrowser.sidebarCollapsed');
    if (saved) {
      setSidebarCollapsed(saved === 'true');
    }
  }, []);

  const toggleSidebar = () => {
    const newValue = !sidebarCollapsed;
    setSidebarCollapsed(newValue);
    localStorage.setItem('dataBrowser.sidebarCollapsed', String(newValue));
  };

  const handleExecute = async (config: RequestConfig) => {
    setExecuting(true);
    setLastRequestConfig(config);

    const startTime = Date.now();

    try {
      let response;
      if (config.method === 'GET') {
        response = await fetchGatewayData(alias, config.endpoint, config.queryParams);
      } else {
        const body = config.body ? JSON.parse(config.body) : undefined;
        response = await postGatewayData(alias, config.endpoint, body, config.queryParams);
      }

      setResult({
        success: response.success,
        data: response.data,
        status: response.status,
        durationMs: response.metadata?.durationMs || Date.now() - startTime,
        timestamp: response.metadata?.timestamp || new Date().toISOString(),
        provider: response.provider,
        endpoint: response.endpoint,
      });
    } catch (err: unknown) {
      const error = err as { statusCode?: number; message?: string };
      setResult({
        success: false,
        data: null,
        status: error.statusCode || 500,
        durationMs: Date.now() - startTime,
        timestamp: new Date().toISOString(),
        provider: connection?.provider_config_key || '',
        endpoint: config.endpoint,
        error: error.message || 'Request failed',
      });
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !connection) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium">{error || 'Connection not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/data' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Connections
        </Button>
      </div>
    );
  }

  const statusColor =
    connection.status === 'active'
      ? 'text-green-500'
      : connection.status === 'expired'
      ? 'text-yellow-500'
      : 'text-red-500';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/data' })}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-semibold">
              {connection.display_name || connection.provider_config_key}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">
                {connection.alias}
              </code>
              <span className={cn('flex items-center gap-1', statusColor)}>
                <CheckCircle2 className="h-3.5 w-3.5" />
                {connection.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sidebar */}
        <div
          className={cn(
            'shrink-0 transition-all duration-200 ease-in-out overflow-hidden',
            sidebarCollapsed ? 'w-0' : 'w-72'
          )}
        >
          <Card className="h-full">
            <CardHeader className="p-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">Endpoints</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-7 w-7 p-0"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </CardHeader>
            <div className="h-[calc(100%-56px)]">
              <EndpointSidebar
                provider={connection.provider_config_key}
                selectedEndpoint={selectedEndpoint}
                onSelectEndpoint={setSelectedEndpoint}
              />
            </div>
          </Card>
        </div>

        {/* Collapsed Sidebar Toggle */}
        {sidebarCollapsed && (
          <Button
            variant="outline"
            size="icon"
            onClick={toggleSidebar}
            className="shrink-0 h-10 w-10"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Main Panel */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {/* Request Builder */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Request</CardTitle>
            </CardHeader>
            <CardContent>
              <RequestBuilder
                selectedEndpoint={selectedEndpoint}
                onExecute={handleExecute}
                loading={executing}
              />
            </CardContent>
          </Card>

          {/* Response */}
          <Card className="flex-1 overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Response</CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-56px)] overflow-auto">
              <ResponseTabs
                result={result}
                requestConfig={lastRequestConfig}
                connectionAlias={alias}
                loading={executing}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
